import { useState, useCallback, useRef, useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Search, Archive, Loader2, Clock, X, Info } from 'lucide-react';
import { PageHeader } from '@/components/shared/PageHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ArchiveQueryResult } from '@/components/archive/ArchiveQueryResult';
import { api } from '@/lib/api';
import type { ArchiveQueryResponse } from '@/types/api';

const EXAMPLE_QUERIES = [
  'What is our current HIPAA breach notification policy?',
  'Show me all infection control procedures updated this year',
  'Which departments were affected by the last FDA recall notice?',
  'What changed in our patient consent forms since January?',
];

const HISTORY_KEY = 'noeted-archive-history';
const MAX_HISTORY = 10;

interface HistoryEntry {
  readonly query: string;
  readonly timestamp: string;
}

function loadHistory(): HistoryEntry[] {
  try {
    const raw = localStorage.getItem(HISTORY_KEY);
    return raw ? (JSON.parse(raw) as HistoryEntry[]) : [];
  } catch {
    return [];
  }
}

function saveHistory(entries: HistoryEntry[]) {
  localStorage.setItem(HISTORY_KEY, JSON.stringify(entries.slice(0, MAX_HISTORY)));
}

export function ArchivePage() {
  const [query, setQuery] = useState('');
  const [result, setResult] = useState<ArchiveQueryResponse | null>(null);
  const [degradedMessage, setDegradedMessage] = useState<string | null>(null);
  const [history, setHistory] = useState<HistoryEntry[]>(loadHistory);
  const [showHistory, setShowHistory] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const queryMutation = useMutation({
    mutationFn: (q: string) => api.archive.query(q),
    onSuccess: (data: ArchiveQueryResponse & { degraded?: boolean; message?: string }, q) => {
      if (data.degraded) {
        setDegradedMessage(data.message ?? 'Archive search is not yet configured for this environment.');
        setResult(null);
        return;
      }
      setDegradedMessage(null);
      setResult(data);
      const entry: HistoryEntry = { query: q, timestamp: new Date().toISOString() };
      const updated = [entry, ...history.filter((h) => h.query !== q)].slice(0, MAX_HISTORY);
      setHistory(updated);
      saveHistory(updated);
      setShowHistory(false);
    },
  });

  const handleSubmit = useCallback(
    (q?: string) => {
      const text = (q ?? query).trim();
      if (!text || queryMutation.isPending) return;
      setQuery(text);
      queryMutation.mutate(text);
    },
    [query, queryMutation],
  );

  const clearHistory = () => {
    setHistory([]);
    saveHistory([]);
  };

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  return (
    <div className="flex h-full flex-col gap-6 overflow-y-auto p-6">
      <PageHeader
        title="Compliance Archive"
        subtitle="Query the complete version history of your compliance documents"
      />

      {/* Search bar */}
      <div className="relative">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              ref={inputRef}
              className="pl-10"
              placeholder="Ask a question about your compliance documents..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSubmit();
              }}
              onFocus={() => setShowHistory(true)}
              onBlur={() => {
                setTimeout(() => setShowHistory(false), 200);
              }}
            />
          </div>
          <Button
            className="bg-teal text-white hover:bg-teal/90"
            disabled={query.trim().length === 0 || queryMutation.isPending}
            onClick={() => handleSubmit()}
          >
            {queryMutation.isPending ? (
              <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
            ) : (
              <Search className="mr-1.5 h-4 w-4" />
            )}
            Search
          </Button>
        </div>

        {/* History dropdown */}
        {showHistory && history.length > 0 && (
          <div className="absolute left-0 right-0 top-full z-50 mt-1 rounded-lg border border-border bg-popover shadow-lg">
            <div className="flex items-center justify-between border-b border-border px-3 py-2">
              <span className="text-xs font-medium text-muted-foreground">
                Recent Queries
              </span>
              <button
                type="button"
                className="text-xs text-muted-foreground hover:text-foreground"
                onMouseDown={(e) => {
                  e.preventDefault();
                  clearHistory();
                }}
              >
                Clear
              </button>
            </div>
            {history.map((entry) => (
              <button
                key={entry.timestamp}
                type="button"
                className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm text-foreground transition-colors hover:bg-muted"
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleSubmit(entry.query);
                }}
              >
                <Clock className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                <span className="truncate">{entry.query}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Degraded mode banner */}
      {degradedMessage && (
        <div className="flex items-start gap-3 rounded-lg border border-teal/30 bg-teal/5 px-4 py-3">
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-teal" />
          <p className="text-sm text-muted-foreground">
            {degradedMessage} Available after full deployment.
          </p>
        </div>
      )}

      {/* Example chips */}
      {!result && !queryMutation.isPending && !degradedMessage && (
        <div>
          <p className="mb-2 text-xs font-medium text-muted-foreground">
            Try an example query:
          </p>
          <div className="flex flex-wrap gap-2">
            {EXAMPLE_QUERIES.map((eq) => (
              <button
                key={eq}
                type="button"
                className="rounded-full border border-border px-3 py-1.5 text-xs text-muted-foreground transition-colors hover:border-teal/40 hover:text-foreground"
                onClick={() => handleSubmit(eq)}
              >
                {eq}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Loading state */}
      {queryMutation.isPending && (
        <div className="flex flex-col items-center gap-3 py-16">
          <Loader2 className="h-8 w-8 animate-spin text-teal" />
          <p className="text-sm text-muted-foreground">
            Searching compliance archive...
          </p>
        </div>
      )}

      {/* Error state */}
      {queryMutation.isError && (
        <div className="flex flex-col items-center gap-3 py-16">
          <X className="h-8 w-8 text-red-400" />
          <p className="text-sm text-red-400">
            {queryMutation.error instanceof Error
              ? queryMutation.error.message
              : 'Search failed. Please try again.'}
          </p>
          <Button variant="outline" size="sm" onClick={() => handleSubmit()}>
            Try Again
          </Button>
        </div>
      )}

      {/* Result */}
      {result && !queryMutation.isPending && (
        <ArchiveQueryResult result={result} />
      )}

      {/* Empty state (initial) */}
      {!result && !queryMutation.isPending && !queryMutation.isError && !degradedMessage && (
        <div className="flex flex-1 flex-col items-center justify-center pb-20">
          <Archive className="mb-4 h-16 w-16 text-muted-foreground/30" />
          <p className="text-lg font-semibold text-foreground">
            Ask anything about your compliance history
          </p>
          <p className="mt-1 max-w-md text-center text-sm text-muted-foreground">
            Search across all document versions with full tamper detection
            verification. Results include source citations and version provenance.
          </p>
        </div>
      )}
    </div>
  );
}
